// ============================================================================
// STUDIO CANETTE — POINT D'ENTRÉE
//
// Ordre imposé, et il compte :
//   1. les feuilles de style, tokens.css en premier — tout le reste consomme
//      ses variables, et un composant chargé avant les jetons s'affiche nu ;
//   2. le thème, avant le premier rendu de composant ;
//   3. la SESSION, avant le routeur. C'est le point crucial : démarrer le
//      routeur sans savoir qui est connecté afficherait l'en-tête « Se
//      connecter » pendant 300 ms à un abonné, puis renverrait /app vers
//      /connexion avant de revenir. On paie un écran de démarrage court plutôt
//      qu'un clignotement et une redirection fantôme ;
//   4. les routes, puis le routeur.
//
// Rien d'autre ne vit ici. Chaque page est un module chargé à la demande.
// ============================================================================

import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';
import './styles/pages.css';
import './styles/studio.css';

import { h, frag } from './lib/dom.js';
import { initTheme } from './lib/theme.js';
import { bootSession } from './lib/session.js';
import { route, startRouter } from './lib/router.js';
import { toastError } from './lib/toast.js';
import { BRAND } from '../shared/copy.js';

/* ------------------------------------------------------------------ *
 * Écran de démarrage                                                  *
 * ------------------------------------------------------------------ */

// Assez long pour ne pas être un flash, assez court pour ne pas se faire
// remarquer : en dessous de ~220 ms, un voile qui apparaît et disparaît est
// perçu comme un défaut d'affichage.
const SPLASH_MIN_MS = 240;
const splashStart = performance.now();

function showSplash() {
  if (document.getElementById('sc-splash')) return;
  document.head.appendChild(h('style#sc-splash-css', {
    text: `
#sc-splash{position:fixed;inset:0;z-index:calc(var(--z-toast) + 10);
  display:grid;place-items:center;background:var(--bg);
  transition:opacity var(--t-slow) var(--ease)}
#sc-splash.is-out{opacity:0;pointer-events:none}
.sc-splash__box{display:grid;justify-items:center;gap:var(--s4);padding:var(--s6) var(--s7);
  background:var(--surface);border:var(--bd-lg);border-radius:var(--r-lg);box-shadow:var(--sh-xl)}
.sc-splash__word{display:flex;align-items:center;gap:var(--s3);
  font-family:var(--font-display);font-size:var(--fs-lg);letter-spacing:-.02em;color:var(--ink)}
.sc-splash__word svg{overflow:visible}
.sc-splash__word svg path{fill:var(--acc);stroke:var(--ink);stroke-width:2;stroke-linejoin:round}
.sc-splash__bar{width:216px;height:14px;overflow:hidden;background:var(--bg-alt);
  border:var(--bd);border-radius:var(--r-pill);box-shadow:var(--sh-xs)}
.sc-splash__bar>i{display:block;width:38%;height:100%;background:var(--acc);
  animation:sc-splash-run 1s var(--ease) infinite}
@keyframes sc-splash-run{0%{transform:translateX(-105%)}100%{transform:translateX(300%)}}
`
  }));

  document.body.appendChild(
    h('div#sc-splash', { role: 'status', 'aria-label': 'Chargement du studio' },
      h('div.sc-splash__box',
        h('div.sc-splash__word',
          frag(`<svg viewBox="0 0 22 28" width="24" height="30" aria-hidden="true">
<path d="M4.6 6.4c0-1.7 1-2.4 1.6-3V2h9.6v1.4c.6.6 1.6 1.3 1.6 3v15.2c0 1.7-1 2.4-1.6 3V26H6.2v-1.4c-.6-.6-1.6-1.3-1.6-3z"/>
</svg>`),
          h('span', { text: BRAND.name })
        ),
        h('div.sc-splash__bar', h('i'))
      )
    )
  );
}

function hideSplash() {
  const node = document.getElementById('sc-splash');
  if (!node) return;
  const wait = Math.max(0, SPLASH_MIN_MS - (performance.now() - splashStart));
  setTimeout(() => {
    node.classList.add('is-out');
    setTimeout(() => node.remove(), 340);
  }, wait);
}

/* ------------------------------------------------------------------ *
 * Table des routes                                                    *
 * ------------------------------------------------------------------ */

function declareRoutes() {
  route('/', () => import('./pages/landing.js'));
  route('/tarifs', () => import('./pages/pricing.js'));

  // Un seul module pour les quatre écrans d'authentification : ils partagent le
  // split-screen et le formulaire, seul le mode change.
  route('/connexion', () => import('./pages/auth.js'), { guestOnly: true, params: { mode: 'signin' } });
  route('/inscription', () => import('./pages/auth.js'), { guestOnly: true, params: { mode: 'signup' } });
  route('/mot-de-passe-oublie', () => import('./pages/auth.js'), { guestOnly: true, params: { mode: 'forgot' } });
  // Pas de guestOnly : le lien de récupération n'ouvre PAS de session (il
  // apporte un jeton dans l'URL), et un utilisateur déjà connecté qui clique le
  // lien de son courriel doit atterrir ici, pas sur /app.
  route('/nouveau-mdp', () => import('./pages/auth.js'), { params: { mode: 'reset' } });

  // Le tableau de bord : la porte d'entrée de l'application. Déclaré AVANT
  // '/app' par lisibilité — c'est là qu'on arrive après connexion.
  route('/app/projets', () => import('./pages/projects.js'), { requireAuth: true });
  // Avant tout projet : pour quel client, quelle gamme, sur quels contenants.
  route('/app/nouveau', () => import('./pages/nouveau.js'), { requireAuth: true });
  route('/app', () => import('./pages/studio.js'), { requireAuth: true });
  // La même coquille, un autre moteur : la bouteille (src/studio/bottlestudio.js).
  // Le paramètre statique `product` est le seul aiguillage.
  route('/app/bouteille', () => import('./pages/studio.js'), { requireAuth: true, params: { product: 'bottle' } });
  // Le carton PLV : même coquille, moteur et dock à lui (src/studio/cartonstudio.js).
  route('/app/carton', () => import('./pages/studio.js'), { requireAuth: true, params: { product: 'carton' } });
  // Supermerch : le vêtement (src/studio/merchstudio.js). Même coquille, moteur et dock à lui.
  route('/app/merch', () => import('./pages/studio.js'), { requireAuth: true, params: { product: 'merch' } });
  route('/app/rendus', () => import('./pages/renders.js'), { requireAuth: true });
  route('/compte', () => import('./pages/account.js'), { requireAuth: true });
  route('/merci', () => import('./pages/thanks.js'), { requireAuth: true });

  // Publique, et volontairement : c'est le lien de bon à tirer qu'un abonné
  // envoie à SON client. Une garde `requireAuth` ici renverrait ce client vers
  // un formulaire de connexion pour un compte qu'il n'a pas et ne veut pas.
  route('/partage/:token', () => import('./pages/share.js'));

  route('/cgu', () => import('./pages/legal.js'), { params: { doc: 'cgu' } });
  route('/confidentialite', () => import('./pages/legal.js'), { params: { doc: 'privacy' } });
  route('/mentions', () => import('./pages/legal.js'), { params: { doc: 'legal' } });
}

/* ------------------------------------------------------------------ *
 * Filet de sécurité                                                   *
 * ------------------------------------------------------------------ */

// Une exception non rattrapée pendant un rendu laisse un écran blanc, sans le
// moindre indice pour l'utilisateur. On préfère un toast : la page reste
// utilisable, et le message part avec la console.
function installErrorNet() {
  let shown = 0;
  const report = err => {
    if (shown++ > 3) return;              // un incident en boucle ne noie pas l'écran
    hideSplash();
    console.error('[studiocanette]', err);
    try { toastError(err); } catch { /* le toast lui-même a lâché : on abandonne */ }
  };

  window.addEventListener('error', e => {
    // Bruit connu et sans conséquence des observateurs de redimensionnement.
    if (/ResizeObserver loop/.test(e.message || '')) return;
    report(e.error || new Error(e.message || 'Erreur inattendue.'));
  });

  window.addEventListener('unhandledrejection', e => {
    const r = e.reason;
    if (r?.name === 'AbortError' || r?.code === 'aborted') return;
    report(r instanceof Error ? r : new Error(String(r ?? 'Promesse rejetée.')));
  });
}

/* ------------------------------------------------------------------ *
 * Démarrage                                                           *
 * ------------------------------------------------------------------ */

async function start() {
  installErrorNet();
  initTheme();
  showSplash();
  declareRoutes();

  try {
    await bootSession();
  } catch (e) {
    // Auth injoignable : on démarre quand même en visiteur. Les pages publiques
    // fonctionnent, et les gardes renverront proprement sur /connexion.
    console.error('[studiocanette] session', e);
  }

  startRouter();
  hideSplash();
}

start();
